"use client";

import { c as _c } from "react/compiler-runtime";
import type { ApiWave } from "@/generated/models/ApiWave";
import { getRandomColorWithSeed, getTimeAgoShort } from "@/helpers/Helpers";
import ContentDisplay from "@/components/waves/drops/ContentDisplay";
import type { ProcessedContent } from "@/components/waves/drops/media-utils";
import { getScaledImageUri, ImageScale } from "@/helpers/image.helpers";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import Image from "next/image";
import Link from "next/link";
import { ChatBubbleLeftIcon } from "@heroicons/react/24/outline";
import { extractDropPreview, useWaveLatestDrop } from "./useWaveLatestDrop";
interface ExploreWaveCardProps {
  readonly wave: ApiWave;
}
export function ExploreWaveCard(t0) {
  const $ = _c(43);
  const {
    wave
  } = t0;
  const t1 = wave.chat.scope.group?.is_direct_message ?? false;
  let t2;
  if ($[0] !== t1 || $[1] !== wave.id) {
    t2 = getWaveRoute({
      waveId: wave.id,
      isDirectMessage: t1,
      isApp: false
    });
    $[0] = t1;
    $[1] = wave.id;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const waveHref = t2;
  const author = wave.author;
  let t3;
  if ($[3] !== author.banner1_color || $[4] !== author.handle) {
    t3 = author.banner1_color ?? getRandomColorWithSeed(author.handle ?? "");
    $[3] = author.banner1_color;
    $[4] = author.handle;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const banner1 = t3;
  let t4;
  if ($[6] !== author.banner2_color || $[7] !== author.handle) {
    t4 = author.banner2_color ?? getRandomColorWithSeed(author.handle ?? "");
    $[6] = author.banner2_color;
    $[7] = author.handle;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const banner2 = t4;
  let t5;
  if ($[9] !== banner1 || $[10] !== banner2 || $[11] !== wave.picture) {
    t5 = !wave.picture ? {
      background: `linear-gradient(135deg, ${banner1} 0%, ${banner2} 100%)`
    } : undefined;
    $[9] = banner1;
    $[10] = banner2;
    $[11] = wave.picture;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const imageAreaStyle = t5;
  const lastMessageTime = wave.metrics.latest_drop_timestamp;
  const hasDrops = wave.metrics.drops_count > 0;
  const {
    data: latestDrop,
    isLoading: isLoadingDrop
  } = useWaveLatestDrop(wave.id, hasDrops);
  const t6 = latestDrop ?? null;
  let t7;
  if ($[13] !== t6) {
    t7 = extractDropPreview(t6);
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const latestMessagePreview = t7;
  const t8 = `View wave ${wave.name}`;
  let t9;
  if ($[15] !== wave.name || $[16] !== wave.picture) {
    t9 = wave.picture && <Image src={getScaledImageUri(wave.picture, ImageScale.AUTOx450)} alt={`${wave.name} cover`} fill={true} sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw" className="tw-object-cover tw-transition-transform tw-duration-700 tw-will-change-transform desktop-hover:group-hover:tw-scale-105" />;
    $[15] = wave.name;
    $[16] = wave.picture;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== imageAreaStyle || $[19] !== t9) {
    t10 = <div className="tw-relative tw-aspect-[3/2] tw-overflow-hidden" style={imageAreaStyle}>{t9}</div>;
    $[18] = imageAreaStyle;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== wave.name) {
    t11 = <span className="tw-m-0 tw-line-clamp-2 tw-text-base tw-font-semibold tw-leading-tight tw-text-white tw-transition-colors group-hover:tw-text-white/80 md:tw-line-clamp-1">{wave.name}</span>;
    $[21] = wave.name;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== hasDrops || $[24] !== lastMessageTime || $[25] !== wave.metrics.drops_count) {
    t12 = hasDrops && <div className="tw-mb-3 tw-mt-2 tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-iron-500"><span className="tw-size-1.5 tw-rounded-full tw-bg-emerald-500" /><span className="tw-text-iron-300">{getTimeAgoShort(lastMessageTime)} ·{" "}{wave.metrics.drops_count.toLocaleString()}</span>{" "}drops</div>;
    $[23] = hasDrops;
    $[24] = lastMessageTime;
    $[25] = wave.metrics.drops_count;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== hasDrops) {
    t13 = !hasDrops && <div className="tw-mb-3 tw-text-[11px] tw-text-iron-500">No drops yet</div>;
    $[27] = hasDrops;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== hasDrops || $[30] !== isLoadingDrop || $[31] !== latestMessagePreview) {
    t14 = hasDrops && <div className="tw-flex tw-items-center tw-gap-3 tw-rounded-lg tw-bg-black tw-p-2 tw-shadow-inner"><div className="tw-flex tw-h-7 tw-w-7 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-iron-900 tw-shadow-sm"><ChatBubbleLeftIcon className="tw-size-3.5 tw-shrink-0 tw-text-iron-400" aria-hidden="true" /></div><MessagePreviewContent isLoading={isLoadingDrop} previewContent={latestMessagePreview} /></div>;
    $[29] = hasDrops;
    $[30] = isLoadingDrop;
    $[31] = latestMessagePreview;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t11 || $[34] !== t12 || $[35] !== t13 || $[36] !== t14) {
    t15 = <div className="tw-p-5">{t11}{t12}{t13}{t14}</div>;
    $[33] = t11;
    $[34] = t12;
    $[35] = t13;
    $[36] = t14;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== t10 || $[39] !== t15 || $[40] !== t8 || $[41] !== waveHref) {
    t16 = <Link href={waveHref} prefetch={false} className="tw-group tw-block tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-white/5 tw-bg-iron-950 tw-text-left tw-no-underline tw-transition-all tw-duration-300 tw-ease-out focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-iron-500/30 desktop-hover:hover:tw-border-white/10" aria-label={t8}>{t10}{t15}</Link>;
    $[38] = t10;
    $[39] = t15;
    $[40] = t8;
    $[41] = waveHref;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  return t16;
}
function MessagePreviewContent(t0) {
  const $ = _c(3);
  const {
    isLoading,
    previewContent
  } = t0;
  if (isLoading) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="tw-h-7 tw-flex-1 tw-animate-pulse tw-rounded tw-bg-iron-800/60" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (!previewContent) {
    return null;
  }
  let t1;
  if ($[1] !== previewContent) {
    t1 = <ContentDisplay content={previewContent} className="tw-line-clamp-1 tw-flex tw-min-w-0 tw-items-center tw-gap-1 tw-text-xs tw-font-medium tw-leading-relaxed tw-text-iron-400" textClassName="tw-line-clamp-1 tw-break-words" />;
    $[1] = previewContent;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}